/**
 * Page Profile Plan Generation Stage
 *
 * Generates a page outline from structured product data.
 * Uses Haiku for fast, non-streaming generation.
 * The LLM assigns images and testimonials by ID during planning.
 */

import type {
  PageProfileInput,
  PageProfileOutline,
  AssignedImage,
  ProfiledImage,
  Review,
} from '../types/page-profile';
import type { ModelOption, AiGenerationStats } from '../types';
import { nonStreamingCompletion } from '../api/anthropic-client';
import {
  buildPageProfilePlanPrompt,
  tryParsePageProfileOutline,
  validatePageProfileOutline,
} from '../prompts/page-profile-plan-prompt';

const MAX_TOKENS = 8000;
const MAX_RETRIES = 1;

/**
 * Resolve LLM-assigned image refs to full image data
 */
const resolveImageRefs = (
  outline: PageProfileOutline,
  images: ProfiledImage[],
): PageProfileOutline => {
  const sectionsWithResolvedImages = outline.sections.map((section) => {
    if (!section.assignedImages || section.assignedImages.length === 0) {
      return { ...section, resolvedImages: [] };
    }

    const resolvedImages: AssignedImage[] = [];

    for (const ref of section.assignedImages) {
      // Parse imageId like "img_0" to get index
      const match = ref.imageId.match(/^img_(\d+)$/);
      if (!match) continue;

      const index = parseInt(match[1], 10);
      if (index < 0 || index >= images.length) continue;

      const img = images[index];
      const meta = img.metadata;

      resolvedImages.push({
        url: img.url,
        alt: meta?.description || 'Image',
        role: ref.role,
        width: meta?.width || 800,
        height: meta?.height || 600,
        fit: ref.fit,
        suggestedBackground: meta?.hasTransparency ? 'rgba(220,220,220,0.2)' : undefined,
        luminance: meta?.luminance,
        brightness: meta?.brightness,
      });
    }

    return { ...section, resolvedImages };
  });

  return { ...outline, sections: sectionsWithResolvedImages };
};

/**
 * Resolve LLM-assigned testimonial refs to full review data
 */
const resolveTestimonialRefs = (
  outline: PageProfileOutline,
  reviews: Review[],
): PageProfileOutline => {
  const sectionsWithResolvedTestimonials = outline.sections.map((section) => {
    if (!section.assignedTestimonials || section.assignedTestimonials.length === 0) {
      return section;
    }

    const resolvedTestimonials: Review[] = [];

    for (const ref of section.assignedTestimonials) {
      // Parse testimonialId like "review_0" to get index
      const match = ref.testimonialId.match(/^review_(\d+)$/);
      if (!match) continue;

      const index = parseInt(match[1], 10);
      if (index < 0 || index >= reviews.length) continue;

      resolvedTestimonials.push(reviews[index]);
    }

    return { ...section, resolvedTestimonials };
  });

  return { ...outline, sections: sectionsWithResolvedTestimonials };
};

/**
 * Generate a page outline from structured product data
 *
 * @param input - The PageProfileInput containing product data
 * @param signal - Optional AbortSignal for cancellation
 * @returns PageProfileOutline containing the structural plan for page generation
 */
export const generatePageProfilePlan = async (
  input: PageProfileInput,
  signal?: AbortSignal,
  model?: ModelOption,
  stats?: AiGenerationStats,
): Promise<PageProfileOutline> => {
  let lastResponseText = '';

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const { system, user } = buildPageProfilePlanPrompt(input, attempt > 0);

    console.log('[PageProfile Plan] Generating outline...');
    console.log('[PageProfile Plan] System prompt:', system);
    console.log('[PageProfile Plan] User prompt:', user);

    const t0 = Date.now();
    const result = await nonStreamingCompletion({
      model: model ?? 'haiku-4.5',
      maxTokens: MAX_TOKENS,
      systemPrompt: system,
      userPrompt: user,
      signal,
    });
    if (stats) {
      stats.planDurationMs = Date.now() - t0;
      stats.totalInputTokens += result.inputTokens;
      stats.totalOutputTokens += result.outputTokens;
      stats.trackStage?.('plan', {
        model: model ?? 'haiku-4.5',
        inputTokens: result.inputTokens,
        outputTokens: result.outputTokens,
        durationMs: stats.planDurationMs,
      });
    }

    lastResponseText = result.text;
    const outline = tryParsePageProfileOutline(result.text);

    if (outline) {
      // Resolve LLM-assigned image refs to full image data
      const outlineWithImages = resolveImageRefs(outline, input.images);

      // Resolve LLM-assigned testimonial refs to full review data
      const finalOutline = resolveTestimonialRefs(
        outlineWithImages,
        input.testimonials?.reviews || [],
      );

      validatePageProfileOutline(finalOutline);

      console.log('[PageProfile Plan] Outline generated successfully');
      console.log(finalOutline);
      return finalOutline;
    }

    console.warn(`[PageProfile Plan] Attempt ${attempt + 1} failed to parse JSON, retrying...`);
  }

  throw new Error(
    `Page profile planning failed: the AI did not return valid JSON after ${MAX_RETRIES + 1} attempts. ` +
      `Response started with: "${lastResponseText.slice(0, 80)}..."`,
  );
};
